import ResponderBadge from '../components/ResponderBadge';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db, auth } from '../firebase';

// Both stat columns put their value in the same fixed-height row so the
// labels under them line up (the gold disc is taller than the rating text).
const STAT_VALUE_ROW = {
  height: '32px', marginBottom: '0.25rem',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

const PublicProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [userData, setUserData] = useState(null);
  const [feedbackData, setFeedbackData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [filterRating, setFilterRating] = useState('all');
  const [loadingFeedback, setLoadingFeedback] = useState(true);

  // Simple back button
  const handleBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    // Check auth state
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
    });

    const fetchUserData = async () => {
      try {
        setLoading(true);
        // PII isolation: read sanitized public mirror (see firestore.rules /publicProfiles).
        const userRef = doc(db, 'publicProfiles', userId);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const data = userSnap.data();
          
          // 🚫 CHECK IF USER IS BANNED - Don't show their profile
          if (data.isBanned === true) {
            console.log('🚫 User is banned - not showing profile');
            setUserData(null); // Treat as user not found
            setLoading(false);
            return;
          }
          
          setUserData({
            username: data.username || 'Unknown User',
            photoURL: data.photoURL || '/images/default-avatar.png',
            completedTrades: data.completedTrades || 0,
            averageRating: data.averageRating || 0,
            totalRatings: data.totalRatings || 0,
            membershipTier: data.membershipTier || 'free',
            aboutMe: data.aboutMe || '',
            // City/state now come from the publicProfiles mirror as flat fields.
            location: (data.locationCity && data.locationState)
              ? `${data.locationCity}, ${data.locationState}`
              : null
          });
        } else {
          setUserData(null); // User not found
        }
      } catch (err) {
        console.error('Failed to load user data:', err);
        setUserData(null);
      } finally {
        setLoading(false);
      }
    };

    const fetchFeedback = async () => {
      try {
        setLoadingFeedback(true);
        // Reads publicTrades, not tradeOffers/purchaseOffers (2026-10-30). This
        // page is public, and until then it ran four whole-document queries on
        // the private source collections to show the reviews someone received.
        // publicTrades is the server-maintained allowlisted mirror (one record
        // per completed trade or purchase, both uids in participantIds), so one
        // query replaces the four and nothing private crosses the wire.
        const snapshot = await getDocs(
          query(collection(db, 'publicTrades'), where('participantIds', 'array-contains', userId))
        );

        const pedalName = (pedal) => `${pedal?.make || ''} ${pedal?.model || ''}`.trim() || null;

        // Legacy records without a pedal snapshot: fall back to the listing
        // (listings are public), as before.
        const listingName = async (listingId) => {
          if (!listingId) return null;
          try {
            const pedalSnap = await getDoc(doc(db, 'listings', listingId));
            return pedalSnap.exists() ? pedalName(pedalSnap.data()) : null;
          } catch (err) {
            console.error('Error fetching pedal for feedback:', err);
            return null;
          }
        };

        const allFeedback = [];

        for (const recordDoc of snapshot.docs) {
          const record = recordDoc.data();

          if (record.kind === 'trade') {
            // fromUserFeedback is written BY fromUser ABOUT toUser, and vice
            // versa, so the review this user RECEIVED is the other side's.
            const isFrom = record.fromUserId === userId;
            const received = isFrom ? record.toUserFeedback : record.fromUserFeedback;
            if (!received) continue;
            const ownPedal = isFrom
              ? record.pedalSnapshots?.initiatorPedals?.[0]
              : record.pedalSnapshots?.responderPedal;
            const tradedPedalName = pedalName(ownPedal)
              || await listingName(isFrom ? record.initiatorPedalId : record.responderPedalId);
            allFeedback.push({
              tradeId: record.sourceId,
              rating: received.rating,
              comment: received.comment,
              submittedAt: received.submittedAt,
              fromUser: record.fromUserId,
              toUser: record.toUserId,
              tradeType: 'trade',
              tradedPedalName
            });
          } else if (record.kind === 'purchase') {
            const isBuyer = record.buyerId === userId;
            const received = isBuyer ? record.sellerFeedback : record.buyerFeedback;
            if (!received || !received.submittedAt) continue;
            const tradedPedalName = pedalName(record.targetPedal) || await listingName(record.targetPedalId);
            allFeedback.push({
              tradeId: record.sourceId,
              rating: received.rating,
              comment: received.comment,
              submittedAt: received.submittedAt,
              fromUser: record.buyerId,
              toUser: record.sellerId,
              tradeType: 'purchase',
              tradedPedalName,
              purchaseRole: isBuyer ? 'buyer' : 'seller'
            });
          }
        }

        // Sort by most recent first
        allFeedback.sort((a, b) => {
          const dateA = a.submittedAt?.toDate?.() || 0;
          const dateB = b.submittedAt?.toDate?.() || 0;
          return dateB.getTime() - dateA.getTime();
        });
        setFeedbackData(allFeedback);
      } catch (err) {
        console.error('Failed to load feedback:', err);
        setFeedbackData([]);
      } finally {
        setLoadingFeedback(false);
      }
    };

    fetchUserData();
    fetchFeedback();
    
    return () => unsubscribe();
  }, [userId]);

  const getTradeBadge = (count) => {
    if (count === undefined || count === null || isNaN(count)) return 0;
    return count;
  };

  // Inline styles only: this pill was written with Tailwind classes, which
  // aren't loaded in this project, so it rendered unstyled as "18VMember"
  // (2026-10-12). Colours match the tier chip on the avatar.
  const getMembershipBadge = (membershipTier) => {
    const pill = {
      display: 'inline-flex', alignItems: 'center', gap: '6px',
      padding: '3px 10px 3px 4px', borderRadius: '9999px',
      fontFamily: 'Nunito, sans-serif', fontSize: '12px', fontWeight: 600, lineHeight: 1.2, whiteSpace: 'nowrap',
    };
    const chip = (bg) => ({
      backgroundColor: bg, color: 'white', borderRadius: '9999px', padding: '2px 7px',
      fontSize: '11px', fontWeight: 800,
    });
    switch (membershipTier) {
      case 'premium_basic':
        return (
          <div style={{ ...pill, backgroundColor: '#DBEAFE', color: '#1E40AF' }}>
            <span style={chip('#2563eb')}>9V</span>
            Member
          </div>
        );
      case 'premium_plus':
        return (
          <div style={{ ...pill, backgroundColor: '#F3E8FF', color: '#6B21A8' }}>
            <span style={chip('#7c3aed')}>18V</span>
            Member
          </div>
        );
      default:
        return (
          <div style={{ ...pill, padding: '3px 10px', backgroundColor: '#f3f4f6', color: '#4b5563' }}>
            Free Member
          </div>
        );
    }
  };

  // Tier tag on the photo's top-left. Sized to its text: the old fixed
  // 20x12px box was narrower than "18V" and squeezed it (2026-10-12).
  const getMembershipProfileBadge = (membershipTier) => {
    const color = membershipTier === 'premium_plus' ? '#7c3aed' : membershipTier === 'premium_basic' ? '#2563eb' : null;
    if (!color) return null;
    return (
      <div
        style={{
          position: 'absolute', top: '-2px', left: '-6px',
          backgroundColor: color, color: 'white', fontWeight: 800,
          fontSize: '10px', lineHeight: 1, padding: '3px 6px', borderRadius: '7px',
          border: '2px solid #fff', boxShadow: '0 0 4px rgba(0,0,0,0.3)',
          fontFamily: 'Nunito, sans-serif', whiteSpace: 'nowrap',
        }}
      >
        {membershipTier === 'premium_plus' ? '18V' : '9V'}
      </div>
    );
  };

  const renderStars = (rating, size = 'text-lg') => {
    const numRating = Number(rating);
    
    return (
      <div className={`flex items-center justify-center ${size}`} style={{ gap: '2px' }}>
        {[1, 2, 3, 4, 5].map((star) => {
          const isYellow = star <= numRating;
          return (
            <span
              key={star}
              style={{ color: isYellow ? '#eab308' : '#d1d5db' }}
            >
              ★
            </span>
          );
        })}
      </div>
    );
  };

  const filteredFeedback = filterRating === 'all' 
    ? feedbackData 
    : feedbackData.filter(feedback => feedback.rating === parseInt(filterRating));

  const formatDate = (timestamp) => {
    if (!timestamp) return 'No date';
    try {
      return timestamp.toDate?.()?.toLocaleDateString() || new Date(timestamp).toLocaleDateString();
    } catch {
      return 'Invalid date';
    }
  };

  if (loading) {
    return (
      <div className='p-4' style={{ backgroundColor: '#F7F3EB', minHeight: 'calc(100vh - var(--navbar-height, 125px))' }}>
        <div className='text-center' style={{ paddingTop: '2rem' }}>Loading profile...</div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className='p-4' style={{ backgroundColor: '#F7F3EB', minHeight: 'calc(100vh - var(--navbar-height, 125px))' }}>
        <div className='text-center' style={{ paddingTop: '2rem' }}>
          <h2 className='text-xl font-bold text-gray-800 mb-4'>User Not Found</h2>
          <button 
            onClick={handleBack} 
            className='text-blue-600 hover:text-blue-800 hover:underline'
          >
            ← Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='public-profile-page' style={{ backgroundColor: '#F7F3EB', minHeight: 'calc(100vh - var(--navbar-height, 125px))', display: 'flex', flexDirection: 'column', padding: '0 1rem' }}>
      <style jsx>{`
        @media (max-width: 768px) {
          .public-profile-page {
            padding: 0 0.75rem !important;
          }
        }
      `}</style>
      
      <div className='mb-4 text-center' style={{ marginTop: '2rem' }}>
        {/* Profile Information Box */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          padding: '1rem',
          marginBottom: '1.5rem',
          maxWidth: '600px',
          margin: '0 auto 1.5rem auto',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          {/* Username */}
          <h2 className='text-2xl font-bold mb-1' style={{ lineHeight: '1.0' }}>
            {userData.username}
          </h2>

          {/* Membership Badge */}
          <div style={{ display: 'flex', justifyContent: 'center', margin: '0.35rem 0 0.75rem 0' }}>
            {getMembershipBadge(userData.membershipTier)}
          </div>

          {/* Profile Image with Badges */}
          <div style={{ position: 'relative', width: '80px', height: '80px', margin: '0 auto 1rem auto', marginTop: '0.75rem' }}>
            <img
              src={userData.photoURL}
              alt='User Avatar'
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                objectFit: 'cover',
                border: '2px solid #ddd',
              }}
            />
            {/* Membership Badge - Top Left */}
            {getMembershipProfileBadge(userData.membershipTier)}
            
            {/* Trade Count Badge - Bottom Right */}
            <div
              style={{
                position: 'absolute',
                bottom: '-4px',
                right: '-4px',
                backgroundColor: 'gold',
                color: '#000',
                fontWeight: 'bold',
                borderRadius: '50%',
                width: '24px',
                height: '24px',
                fontSize: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid #fff',
                boxShadow: '0 0 6px rgba(0,0,0,0.3)',
              }}
            >
              {getTradeBadge(userData.completedTrades)}
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}><ResponderBadge profile={userData} /></div>

          {/* Stats Row */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
            <div style={{ textAlign: 'center' }}>
              {/* Same gold disc as the count on the profile photo, so the
                  number there reads as "completed transactions". */}
              <div style={STAT_VALUE_ROW}>
                <span style={{
                  minWidth: '28px', height: '28px', padding: '0 7px', boxSizing: 'border-box',
                  borderRadius: '9999px', backgroundColor: 'gold', color: '#000', fontWeight: 'bold', fontSize: '0.95rem',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  border: '2px solid #fff', boxShadow: '0 0 6px rgba(0,0,0,0.3)',
                }}>
                  {getTradeBadge(userData.completedTrades)}
                </span>
              </div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: '500' }}>
                Completed Transactions
              </div>
            </div>
            <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {userData.averageRating > 0 ? (
                <>
                  <div style={{ ...STAT_VALUE_ROW, fontSize: '1.25rem', fontWeight: 'bold', color: '#374151' }}>
                    {userData.averageRating.toFixed(1)}/5
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: '500' }}>
                    Average Rating
                  </div>
                </>
              ) : (
                <>
                  <div style={{ ...STAT_VALUE_ROW, fontSize: '1.25rem', fontWeight: 'bold', color: '#9ca3af' }}>
                    No Ratings
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: '500' }}>
                    Yet
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Stars centered relative to profile image */}
          {userData.averageRating > 0 && (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
              {[1, 2, 3, 4, 5].map((star) => (
                <span
                  key={star}
                  style={{ 
                    color: star <= userData.averageRating ? '#fbbf24' : '#d1d5db',
                    fontSize: '1rem',
                    marginRight: '2px'
                  }}
                >
                  ★
                </span>
              ))}
            </div>
          )}

          {/* Location */}
          {userData.location && (
            <div className='text-center mb-4'>
              <div className='text-sm text-gray-600' style={{ fontFamily: 'Nunito' }}>
                📍 {userData.location}
              </div>
            </div>
          )}

          {/* View Pedalboard and Wantlist Links */}
          <div 
            className="profile-action-buttons"
            style={{ 
              textAlign: 'center', 
              marginTop: '1.5rem', 
              paddingTop: '1rem', 
              borderTop: '1px solid #e5e7eb', 
              display: 'flex', 
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: '0.75rem',
              justifyContent: 'center'
            }}
          >
            <style jsx="true">{`
              @media (max-width: 768px) {
                .profile-action-buttons {
                  flex-direction: column !important;
                }
              }
            `}</style>
            <button
              onClick={() => navigate(`/pedalboard/${userId}`)}
              style={{
                backgroundColor: '#5A9ECB',
                color: 'white',
                border: 'none',
                borderRadius: '9999px',
                padding: '8px 20px',
                fontFamily: 'Nunito, sans-serif',
                fontWeight: 'bold',
                fontSize: '15px',
                cursor: 'pointer',
                flex: '0 1 auto'
              }}
              onMouseEnter={(e) => e.target.style.opacity = '0.8'}
              onMouseLeave={(e) => e.target.style.opacity = '1'}
            >
              View {userData.username}'s Pedalboard
            </button>
            <button
              onClick={() => navigate(`/wantlist/${userId}`)}
              style={{
                backgroundColor: '#C89F3C',
                color: 'white',
                border: 'none',
                borderRadius: '9999px',
                padding: '8px 20px',
                fontFamily: 'Nunito, sans-serif',
                fontWeight: 'bold',
                fontSize: '15px',
                cursor: 'pointer',
                flex: '0 1 auto'
              }}
              onMouseEnter={(e) => e.target.style.opacity = '0.8'}
              onMouseLeave={(e) => e.target.style.opacity = '1'}
            >
              View {userData.username}'s Wantlist
            </button>
          </div>
        </div>

        {/* About Me Section */}
        {userData.aboutMe && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            padding: '1rem',
            marginBottom: '1.5rem',
            maxWidth: '600px',
            margin: '0 auto 1.5rem auto',
            width: '100%',
            boxSizing: 'border-box'
          }}>
            <h3 className='text-lg font-bold mb-3' style={{ textAlign: 'center', fontFamily: 'Nunito' }}>
              About {userData.username}
            </h3>
            <div style={{
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              padding: '1.5rem',
              backgroundColor: '#f9fafb',
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word'
            }}>
              {userData.aboutMe}
            </div>
          </div>
        )}

        {/* Feedback Section */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          padding: '1rem',
          marginBottom: '1.5rem',
          maxWidth: '600px',
          margin: '0 auto 1.5rem auto',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <h3 className='text-lg font-bold mb-4' style={{ textAlign: 'center', fontFamily: 'Nunito' }}>
            Feedback ({feedbackData.length})
          </h3>

          {/* Filter Controls */}
          <div className='flex justify-center flex-wrap' style={{ gap: '0.5rem', marginBottom: '1.5rem', justifyContent: 'center' }}>
            <button
              onClick={() => setFilterRating('all')}
              className="hover:opacity-80 transition-opacity"
              style={{
                backgroundColor: '#C9A227',
                borderRadius: '9999px',
                border: 'none',
                padding: '6px 12px',
                fontFamily: 'Nunito, sans-serif',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '14px',
                marginRight: '1rem'
              }}
            >
              All
            </button>
            {[5, 4, 3, 2, 1].map(rating => (
              <button
                key={rating}
                onClick={() => setFilterRating(rating.toString())}
                className='text-sm text-gray-700 hover:opacity-80 transition-opacity'
                style={{
                  backgroundColor: '#E5E7EB',
                  borderRadius: '9999px',
                  border: 'none',
                  padding: '5.75px 13.8px',
                  fontFamily: 'Nunito, sans-serif',
                  color: '#374151',
                  marginRight: '4px'
                }}
              >
                {rating}★
              </button>
            ))}
          </div>

          {/* Feedback List */}
          {loadingFeedback ? (
            <div className='text-center py-4'>Loading feedback...</div>
          ) : filteredFeedback.length === 0 ? (
            <div className='text-center py-4 text-gray-500'>
              {filterRating === 'all' 
                ? 'No feedback received yet' 
                : `No ${filterRating}-star feedback found`
              }
            </div>
          ) : (
            <div className='space-y-3'>
              {filteredFeedback.map((feedback, index) => (
                <div
                  key={`${feedback.tradeId}-${index}`}
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '1.5rem',
                    backgroundColor: '#f9fafb'
                  }}
                >
                  <div className='flex justify-between items-start mb-2'>
                    <div className='flex items-center gap-2'>
                      {renderStars(feedback.rating, 'text-base')}
                      <span className='text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded-full'>
                        {feedback.tradeType === 'purchase' ? 'Purchase' : 'Trade'}
                      </span>
                    </div>
                    <div className='text-xs text-gray-500'>
                      {formatDate(feedback.submittedAt)}
                    </div>
                  </div>
                  {feedback.comment && (
                    <div className='text-gray-700 text-sm' style={{ whiteSpace: 'pre-wrap' }}>
                      "{feedback.comment}"
                    </div>
                  )}
                  {feedback.tradedPedalName && (
                    <div className='text-xs text-gray-500' style={{ marginTop: '0.5rem', fontStyle: 'italic' }}>
                      {feedback.tradeType === 'purchase' 
                        ? (feedback.purchaseRole === 'seller' ? `Sold: ${feedback.tradedPedalName}` : `Purchased: ${feedback.tradedPedalName}`)
                        : `Traded: ${feedback.tradedPedalName}`
                      }
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Back Button */}
      <div className='text-center mt-6 mb-4'>
        <button 
          onClick={handleBack} 
          className='text-blue-600 hover:text-blue-800 hover:underline'
          style={{
            background: 'none', 
            border: 'none', 
            padding: '8px 0', 
            font: 'inherit',
            cursor: 'pointer',
            textDecoration: 'none',
            color: '#5A9ECB',
            fontSize: '16px',
            fontWeight: '500'
          }}
        >
          ← Back
        </button>
      </div>

      {/* Fixed gap below content */}
      <div style={{ height: '100px' }}></div>
    </div>
  );
};

export default PublicProfile;
